import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scale, Loader2, LogOut } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import LegalText from "@/components/LegalText";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DisclaimerGate() {
  const { logout, clearDisclaimer } = useAuth();
  const [reachedEnd, setReachedEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  const endRef = useRef(null);
  const onScroll = () => {
    const el = boxRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setReachedEnd(true);
  };

  // The end marker becoming visible counts too (covers zoom, short screens and content that doesn't scroll)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setReachedEnd(true), { root: boxRef.current, threshold: 0.1 });
    if (endRef.current) obs.observe(endRef.current);
    return () => obs.disconnect();
  }, []);

  const accept = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/auth/disclaimer`, {}, { withCredentials: true });
      clearDisclaimer();
    } catch { toast.error("Could not save. Please try again"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-bt-bg/95 backdrop-blur-md flex items-center justify-center p-3 sm:p-6" data-testid="disclaimer-gate">
      <div className="w-full max-w-2xl bg-bt-surface border border-bt-gold/40 shadow-2xl flex flex-col max-h-[92vh]">
        <div className="px-6 pt-6 pb-3 border-b border-bt-border-subtle">
          <div className="flex items-center gap-3">
            <Scale className="w-5 h-5 text-bt-gold" />
            <h1 className="font-heading text-2xl text-bt-text-primary">Before you start</h1>
          </div>
          <p className="text-sm text-bt-text-secondary mt-1">Please read our disclaimer. You only need to do this once.</p>
        </div>
        <div ref={boxRef} onScroll={onScroll} className="px-6 py-5 overflow-y-auto flex-1" data-testid="disclaimer-scroll">
          <LegalText />
          <div ref={endRef} className="h-px" data-testid="disclaimer-end" />
        </div>
        <div className="px-6 py-4 border-t border-bt-border-subtle space-y-3">
          {!reachedEnd && <p className="text-[11px] font-mono text-bt-text-tertiary" data-testid="disclaimer-scroll-hint">Scroll to the end to continue ↓</p>}
          <label className={`flex items-start gap-2 text-sm ${reachedEnd ? "text-bt-text-primary cursor-pointer" : "text-bt-text-tertiary"}`}>
            <input type="checkbox" disabled={!reachedEnd} checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-[rgb(var(--bt-gold))]" data-testid="disclaimer-agree" />
            I have read and understand that Rockafeller Jones is for personal analysis only, is not financial or professional advice, and that its operators are not liable for any losses.
          </label>
          <div className="flex items-center justify-between gap-3">
            <button onClick={logout} className="flex items-center gap-1 text-xs font-mono text-bt-text-tertiary hover:text-bt-text-primary" data-testid="disclaimer-logout">
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
            <button onClick={accept} disabled={!agreed || saving} data-testid="disclaimer-accept"
              className="flex items-center gap-2 px-5 h-10 bg-bt-gold text-bt-bg font-mono text-sm font-semibold hover:bg-bt-gold/85 disabled:opacity-40">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} I agree, continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
